import React, { useEffect, useMemo } from 'react';
import PageBanner from '../layout/PageBanner';
import Button from '../ui/Button';
import { pastryProducts } from '../../data/products';
import { useBetaProgram } from '../../beta/hooks';
import { useBetaMixDrafts } from '../../beta/mixDrafts';

const getPastrySampleId = (productId = '') => {
  if (productId.includes('mousse')) {
    return 'sample-mousse';
  }

  return 'sample-creme-brulee';
};

const buildOrderSamplePath = (sampleId, source) => {
  const params = new URLSearchParams();
  params.set('sample', sampleId);
  params.set('from', source);
  return `/samples?${params.toString()}`;
};

const PastryMixesPage = () => {
  const { programSlug, program } = useBetaProgram();
  const { ensureManagedMixes, getManagedMixes } = useBetaMixDrafts();
  const showSampleOrdering = Boolean(program?.features?.sampleOrderingFlow);
  const showManageAdmin =
    Boolean(program?.features?.easyManageAdmin) || Boolean(program?.features?.easyAddAdmin);

  useEffect(() => {
    if (!showManageAdmin || !programSlug) {
      return;
    }

    ensureManagedMixes(programSlug, 'pastry', pastryProducts);
  }, [ensureManagedMixes, programSlug, showManageAdmin]);

  const allPastryProducts = useMemo(() => {
    if (!showManageAdmin) {
      return pastryProducts;
    }

    return getManagedMixes(programSlug, 'pastry', pastryProducts);
  }, [getManagedMixes, programSlug, showManageAdmin]);

  return (
    <div>
      <PageBanner
        title="PASTRY MIXES"
        breadcrumbs={[{ label: 'PASTRY MIXES' }]}
      />

      {/* Introduction */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 leading-relaxed text-lg">
            Mirage specializes in the formulation of customized, gourmet dessert mixes
            designed to improve the efficiency of your production kitchen and increase your bottom line.
            Our premium bases deliver consistent results every time.
          </p>
          {showSampleOrdering && (
            <div className="mt-6">
              <Button href="/samples" variant="filled">
                Order Samples
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Products List */}
      <section className="py-8 px-6">
        <div className="max-w-6xl mx-auto space-y-16">
          {allPastryProducts.map((product, index) => (
            <div
              key={product.id}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-8 items-center`}
            >
              <div className="md:w-1/3">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <div className="md:w-2/3">
                <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">
                  {product.title}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  {product.description}
                </p>
                {showSampleOrdering && (
                  <div className="mb-4">
                    <Button
                      href={buildOrderSamplePath(getPastrySampleId(product.id), 'pastry-mixes')}
                      variant="outlined"
                    >
                      Order Sample
                    </Button>
                  </div>
                )}
                {(product.packaging || product.netWeight) && (
                  <div className="text-sm text-gray-500 space-y-1 border-t pt-4">
                    {product.packaging && (
                      <p><strong>Packaging:</strong> {product.packaging}</p>
                    )}
                    {product.netWeight && (
                      <p><strong>Net Weight:</strong> {product.netWeight}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PastryMixesPage;
